import { ConfigurationService, ConfigurationCacheInvalidator } from "sentec-core-ts";
import signalGo from "@/services/SignalGo";
import ApiConfig from "@/services/Api-config";

export interface FnbOrderPayload {
  event: string;
  action?: string; // "created" | "canceled" | "undo_canceled" | "assigned_to_table"
  order_id: number;
  hotel_id: number;
  outlet_id: number;
  guest_name: string;
  guest_room_table: string;
  item_count?: number;
  total_qty?: number;
  table_id?: number;
  created_at?: string;
}

/** Event name emitted on $root when an order notification arrives. */
export const FNB_ORDER_EVENT = "fnb:order-event";

interface OutletNotificationSettings {
  audio: HTMLAudioElement | null;
  dashboard: boolean;
  push: boolean;
}

/**
 * Manages hotel-level FnB order notification subscriptions.
 * Instantiated once in App.vue — lives for the entire session.
 */
export class OrderNotificationService {
  private vm: any; // Vue root instance
  private enabled = false;
  private settingsByOutlet = new Map<number, OutletNotificationSettings>();
  private fallbackSettings: OutletNotificationSettings | null = null;
  private subscribed = false;
  private handler: ((e: any) => void) | null = null;
  private unregisterOnChange: (() => void) | null = null;
  private currentHotelId = 0;
  // Dedupe map: order_id:action → timestamp of first receipt. Prevents duplicate
  // SignalR redeliveries from double-firing while letting distinct orders through.
  private seenEvents = new Map<string, number>();
  // Per-key timestamp of last sound play — throttles audio only, not notifications.
  private lastSoundAt = new Map<string, number>();
  private static readonly DEDUPE_TTL_MS = 5000;
  private static readonly SOUND_THROTTLE_MS = 1000;

  constructor(vm: any) {
    this.vm = vm;
  }

  private get hotelId(): number {
    return Number(this.vm.$route?.query?.hotelId) || 0;
  }

  private get outletId(): number {
    return Number(this.vm.$route?.query?.outletId) || 0;
  }

  private get topic(): string {
    return `fnb_order_${this.hotelId}`;
  }

  private get config(): ConfigurationService | null {
    return this.vm.$config || null;
  }

  /**
   * Initialize or re-initialize for the current hotel.
   * Call on mount and whenever the hotel changes.
   */
  async init(): Promise<void> {
    const hotelId = this.hotelId;
    if (!hotelId || !this.config) return;

    // If hotel changed, tear down old subscription first
    if (this.currentHotelId && this.currentHotelId !== hotelId) {
      this.destroy();
    }
    this.currentHotelId = hotelId;

    await this.loadConfig();
    this.syncSubscription();

    // Re-read config when another user changes it
    if (!this.unregisterOnChange) {
      this.unregisterOnChange = ConfigurationCacheInvalidator.onChange(
        hotelId,
        async () => {
          await this.loadConfig();
          this.syncSubscription();
        }
      );
    }
  }

  destroy(): void {
    if (this.subscribed) {
      signalGo.Unsubscribe(`fnb_order_${this.currentHotelId}`);
      this.subscribed = false;
      this.handler = null;
    }
    if (this.unregisterOnChange) {
      this.unregisterOnChange();
      this.unregisterOnChange = null;
    }
    this.seenEvents.clear();
    this.lastSoundAt.clear();
    this.settingsByOutlet.clear();
    this.fallbackSettings = null;
  }

  private syncSubscription(): void {
    if (this.enabled && !this.subscribed) {
      this.handler = (e: any) => this.handleEvent(e);
      signalGo.Subscribe(this.topic, this.handler);
      this.subscribed = true;
    } else if (!this.enabled && this.subscribed) {
      signalGo.Unsubscribe(this.topic);
      this.subscribed = false;
      this.handler = null;
    }
  }

  private async loadConfig(): Promise<void> {
    const hotelId = this.hotelId;
    const currentOutletId = this.outletId;
    const outlets: any[] = this.vm.$store?.state?.app?.outlets || [];
    const hotelOutlets = outlets.filter((o: any) => o.hotel_id === hotelId);

    const configKeys = [
      "order_notification_enabled",
      "order_notification_sound",
      "order_notification_dashboard",
      "order_notification_push",
    ];

    const outletIds = hotelOutlets.length > 0
      ? hotelOutlets.map((o: any) => o.id as number)
      : (currentOutletId ? [currentOutletId] : []);

    if (outletIds.length === 0) return;

    const results = await Promise.all(
      outletIds.map(async (id: number) => {
        const svc = this.config!.forKeys(configKeys, "outlet", id);
        const enabled = await svc.getBool("order_notification_enabled");
        return { id, enabled, svc };
      })
    );

    const enabledOutlets = results.filter(r => r.enabled);
    this.enabled = enabledOutlets.length > 0;

    if (!this.enabled) return;

    // PMS-623: these settings are per outlet, and one page receives every outlet's orders
    const settings = await Promise.all(
      enabledOutlets.map(async (r) => {
        const soundCfg = await r.svc.getString("order_notification_sound");

        return {
          id: r.id,
          value: {
            audio: this.buildAudio(soundCfg),
            dashboard: await r.svc.getBool("order_notification_dashboard"),
            push: await r.svc.getBool("order_notification_push"),
          } as OutletNotificationSettings,
        };
      })
    );

    this.settingsByOutlet = new Map(settings.map(s => [s.id, s.value]));

    // PMS-623: an outlet added after load still has to notify, so keep the old hotel-wide pick
    const current = this.settingsByOutlet.get(currentOutletId);
    this.fallbackSettings = current || settings[0].value;

    if (settings.some(s => s.value.push) && Notification.permission === "default") {
      await Notification.requestPermission();
    }
  }

  private buildAudio(soundCfg: string | null): HTMLAudioElement | null {
    if (soundCfg === "false") return null;

    if (soundCfg && soundCfg !== "true" && this.isValidAudioPath(soundCfg)) {
      const baseUrl = (ApiConfig.ST_STORAGE_STATIC_URL || "").replace(/\/+$/, "");
      const path = soundCfg.startsWith("/") ? soundCfg : `/${soundCfg}`;
      // PMS-623: encode segments so a space in the key doesn't break the url
      const encoded = path.split("/").map(encodeURIComponent).join("/");
      return new Audio(`${baseUrl}${encoded}`);
    }

    return new Audio("/audio/screen-notification.mp3");
  }

  private settingsFor(outletId: number): OutletNotificationSettings | null {
    return this.settingsByOutlet.get(outletId) || this.fallbackSettings;
  }

  private handleEvent(e: any): void {
    if (!e) return;

    const payload: FnbOrderPayload = e;
    const action = payload.action || "created";
    const key = `${payload.order_id}:${action}`;
    const now = Date.now();

    this.pruneSeen(now);
    if (this.seenEvents.has(key)) return;
    this.seenEvents.set(key, now);

    const settings = this.settingsFor(payload.outlet_id);

    if (settings?.audio) {
      const lastSound = this.lastSoundAt.get(key) ?? 0;
      if (now - lastSound >= OrderNotificationService.SOUND_THROTTLE_MS) {
        this.lastSoundAt.set(key, now);
        settings.audio.currentTime = 0;
        // PMS-623: ring the bundled default for this same event if the custom file fails to load/decode
        settings.audio.play().catch(() => {
          const fallback = new Audio("/audio/screen-notification.mp3");
          fallback.play().catch(() => {});
        });
      }
    }

    const outlets: any[] = this.vm.$store?.state?.app?.outlets || [];
    const outlet = outlets.find((o: any) => o.id === payload.outlet_id);
    const outletName = outlet?.name || "";

    const messageParams = {
      guest: payload.guest_name,
      outlet: outletName,
      location: payload.guest_room_table,
      orderId: payload.order_id,
      count: payload.item_count,
      qty: payload.total_qty,
    };

    const messageKeyMap: Record<string, string> = {
      created: "orderNotification.newOrderMessage",
      canceled: "orderNotification.orderCanceledMessage",
      undo_canceled: "orderNotification.orderUndoCanceledMessage",
      assigned_to_table: "orderNotification.orderAssignedToTableMessage",
    };

    const messageKey = messageKeyMap[action] || messageKeyMap.created;
    const message = this.vm.$t(messageKey, messageParams).toString();

    if (settings?.dashboard) {
      this.vm.$archNotify({
        message,
        color: "primary",
        showClose: true,
        timeout: 8000,
      });
    }

    if (settings?.push) {
      if (Notification.permission === "granted") {
        const notif = new Notification(
          this.vm.$t("orderNotification.newOrderTitle").toString(),
          {
            body: message,
            icon: "/img/icons/apple-touch-icon-152x152.png",
            tag: `fnb-order-${payload.order_id}-${action}`,
          }
        );
        notif.onclick = () => {
          window.focus();
          notif.close();
        };
      } else if (Notification.permission === "default") {
        Notification.requestPermission();
      }
    }

    // Emit on $root so pages can react (e.g., refresh data table)
    this.vm.$root.$emit(FNB_ORDER_EVENT, payload);
  }

  private pruneSeen(now: number): void {
    for (const [k, ts] of this.seenEvents) {
      if (now - ts > OrderNotificationService.DEDUPE_TTL_MS) {
        this.seenEvents.delete(k);
      }
    }
    for (const [k, ts] of this.lastSoundAt) {
      if (now - ts > OrderNotificationService.DEDUPE_TTL_MS) {
        this.lastSoundAt.delete(k);
      }
    }
  }

  private isValidAudioPath(path: string): boolean {
    return /\.(mp3|wav|ogg)$/i.test(path) && !path.includes("..");
  }
}
